import { useMemo, useState } from 'react'
import { Check, Eye } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import type { CaseDetail } from '../types'
import { InvestigationActionDialog } from './InvestigationActionDialog'

/**
 * Investigation Actions Checklist — the catalogue's case-detail
 * sidebar panel that walks the analyst through the canonical
 * disposition steps with a progress badge.
 *
 * v1 derives the checklist deterministically from the case state:
 *   - "Review BVN & KYC limits" — auto-checked iff at least one
 *     linked alert carries a BVN match flag.
 *   - "Verify profile change log" — checked iff the case timeline
 *     contains a profile-change event.
 *   - "Analyze channel velocity" — checked iff Velocity evidence
 *     hit ≥ HIGH (uses the same heuristics as KeyEvidenceCards).
 *   - "Check counterparty network" — checked iff Counterparty
 *     evidence hit ≥ HIGH.
 *   - "Final fraud disposition" — checked iff the case status is
 *     terminal (closed / fraud_confirmed / false_positive).
 *
 * The toggle on each row is a local override for the analyst —
 * persistence to a per-case checklist table is a follow-up. We
 * keep the local toggle so the panel feels responsive in v1.
 */
export function InvestigationActionsChecklist({ c }: { c: CaseDetail }) {
  const items = useMemo(() => deriveItems(c), [c])
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})
  // Which action's drill-down dialog is open. null when closed.
  const [openKey, setOpenKey] = useState<string | null>(null)

  const checked = items.filter((it) => overrides[it.key] ?? it.done).length
  const openItem = items.find((it) => it.key === openKey) ?? null

  return (
    <Card>
      <CardHeader>
        <div className="flex items-baseline justify-between gap-2">
          <CardTitle>Investigation actions</CardTitle>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.6875rem] text-muted-fg">
            {checked} / {items.length}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-muted">
          <span
            className="block h-full bg-primary transition-all"
            style={{ width: `${(checked / items.length) * 100}%` }}
            aria-hidden
          />
        </div>
        <ul className="space-y-2">
          {items.map((it) => {
            const isDone = overrides[it.key] ?? it.done
            return (
              <li key={it.key} className="flex items-start gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setOverrides((o) => ({ ...o, [it.key]: !isDone }))
                  }
                  className="flex flex-1 items-start gap-2 rounded px-1 py-1 text-left hover:bg-muted/40"
                  aria-label={`Toggle ${it.label}`}
                >
                  <span
                    className={
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ' +
                      (isDone
                        ? 'border-status-success bg-status-success/15 text-status-success'
                        : 'border-border')
                    }
                    aria-hidden
                  >
                    {isDone ? <Check size={11} strokeWidth={3} /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={
                        'block text-xs font-medium leading-tight ' +
                        (isDone ? 'text-muted-fg line-through' : '')
                      }
                    >
                      {it.label}
                    </span>
                    <span className="mt-0.5 block text-[0.6875rem] leading-tight text-muted-fg">
                      {it.detail}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setOpenKey(it.key)}
                  className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-fg hover:bg-muted/40 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={`View data for: ${it.label}`}
                  title="View supporting data"
                >
                  <Eye size={12} aria-hidden />
                </button>
              </li>
            )
          })}
        </ul>
      </CardContent>
      {openItem ? (
        <InvestigationActionDialog
          open={openKey !== null}
          onOpenChange={(o) => setOpenKey(o ? openKey : null)}
          actionKey={openItem.key}
          title={openItem.label}
          description={openItem.detail}
          c={c}
        />
      ) : null}
    </Card>
  )
}

interface Item {
  key: string
  label: string
  detail: string
  done: boolean
}

function deriveItems(c: CaseDetail): Item[] {
  const alerts = c.linked_alerts
  const timeline = c.timeline ?? []

  const hasBvnFlag = alerts.some((a) => {
    const flags = (a as unknown as { flags?: string[] }).flags ?? []
    return flags.some((f) => /bvn|kyc/i.test(f))
  })
  const hasProfileChange =
    timeline.some((t) => /profile|reset|pin|kyc/i.test(t.message)) ||
    alerts.some((a) => {
      const f = (a as unknown as { flags?: string[] }).flags ?? []
      return f.some((x) => /profile_change|pin_reset/i.test(x))
    })

  // Reuse the KeyEvidence heuristic: if velocity / counterparty hit
  // HIGH or above, the corresponding step is "done" by analysis.
  const velocityHigh = veloAtHigh(alerts)
  const counterpartyHigh = counterpartyAtHigh(alerts)

  const terminal =
    c.status === 'resolved' ||
    c.status === 'closed' ||
    c.status === 'fraud_confirmed' ||
    c.status === 'false_positive'

  return [
    {
      key: 'bvn_kyc',
      label: 'Review BVN & KYC limits',
      detail: hasBvnFlag ? 'Verified by system' : 'Confirm BVN against current watchlists.',
      done: hasBvnFlag,
    },
    {
      key: 'profile_change',
      label: 'Verify profile change log',
      detail: hasProfileChange
        ? 'Profile / PIN reset detected on the case timeline.'
        : 'Check for recent profile or PIN changes pre-incident.',
      done: hasProfileChange,
    },
    {
      key: 'velocity',
      label: 'Analyze channel velocity',
      detail: velocityHigh
        ? 'Volume / rate flagged above HIGH baseline.'
        : 'Review velocity trends across linked transactions.',
      done: velocityHigh,
    },
    {
      key: 'counterparty',
      label: 'Check counterparty network',
      detail: counterpartyHigh
        ? 'Shared counterparties across alerts — cluster signal.'
        : 'Confirm beneficiary BVNs + recent activity.',
      done: counterpartyHigh,
    },
    {
      key: 'disposition',
      label: 'Final fraud disposition',
      detail: terminal
        ? `Closed as ${c.status.replace('_', ' ')}.`
        : 'Decide between approve / reject / escalate.',
      done: terminal,
    },
  ]
}

function veloAtHigh(alerts: CaseDetail['linked_alerts']): boolean {
  let total = 0
  for (const a of alerts) {
    const m = (a as unknown as { transaction?: { amount_minor?: number } }).transaction?.amount_minor
    if (typeof m === 'number') total += m
  }
  return alerts.length >= 3 || total >= 100_000_00 // ≥3 alerts or ≥₦100k
}

function counterpartyAtHigh(alerts: CaseDetail['linked_alerts']): boolean {
  const counts = new Map<string, number>()
  for (const a of alerts) {
    const ben = (a as unknown as { transaction?: { beneficiary_nuban?: string } }).transaction
      ?.beneficiary_nuban
    if (ben) counts.set(ben, (counts.get(ben) ?? 0) + 1)
  }
  for (const n of counts.values()) {
    if (n >= 2) return true
  }
  return false
}
